import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'utils/dayjs';
import debounce from 'lodash.debounce';

import { fetchCurrentNbuCurr } from 'actions';
import {
  DEFAULT_API_DATE_FORMAT,
  ONLY_CYRILLIC,
  ONLY_ENGLISH,
  ONLY_NUMBERS,
  parseDate,
  formatParsedDate,
} from 'utils';

import { Table } from 'components/Table';
import Icon from 'components/commons/Icon';

import {
  Wrapper,
  Title,
  TitleWrapper,
  Desc,
  Update,
  InputWrapper,
  SearchInput,
} from './styles';
import { TableFooter } from '../Table/styles';
import { Source } from '../CurrencyNbuTable/styles';

const titles = ['code', 'сurrency', 'name', 'course'];

const getHref = currency => `/nbu/${currency}/`;

const filterByType = data => {
  const filteredData = { daily: [], monthly: [] };

  if (data) {
    data.forEach(item => {
      const { type } = item;
      if (type === 'daily') {
        filteredData.daily = [...filteredData.daily, item];
      } else if (type === 'monthly') {
        filteredData.monthly = [...filteredData.monthly, item];
      }
    });
  }

  return filteredData;
};

const AnotherCurrTable = () => {
  const [focus, setFocus] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { date, currency } = useParams();
  const { locale, formatMessage } = useIntl();

  const data = useSelector(state =>
    filterByType(state.currency.nbuCurrencyList.data)
  );

  const [currencyCurr, setCurrencyCurr] = useState(data);

  const currNamePlaceholder = formatMessage({
    id: 'currency.name.placeholder',
  });

  const getCur = params => {
    fetchCurrentNbuCurr(params)
      .then(({ data }) => filterByType(data))
      .then(setCurrencyCurr);
  };

  const onSearch = useCallback(debounce(getCur, 500), []);

  useEffect(() => {
    onSearch(params);
  }, [date, searchText]);

  const getUrl = useMemo(() => {
    if (!searchText) {
      return '';
    }

    let url = '';

    const text = searchText.toLowerCase().split(' ');

    text.forEach(el => {
      if (ONLY_CYRILLIC.test(el)) {
        url = url ? `${url}&filter[name][]=${el}` : `${url}&filter[name]=${el}`;
      } else if (ONLY_ENGLISH.test(el)) {
        url = url ? `${url}&filter[code][]=${el}` : `${url}&filter[code]=${el}`;
      } else if (ONLY_NUMBERS.test(el)) {
        url = url ? `${url}&filter[iso][]=${el}` : `${url}&filter[iso]=${el}`;
      }
    });

    return url;
  }, [searchText]);

  const params = {
    date: (parseDate(date) || dayjs()).format(DEFAULT_API_DATE_FORMAT),
    searchText: getUrl,
    locale,
  };

  return (
    <Wrapper id="anotherCurr">
      <Title>
        <FormattedMessage
          id={date ? 'anotherCurr.date' : 'anotherCurr'}
          values={{
            date: formatParsedDate(date, 'DD MMMM YYYY', locale),
          }}
        />
      </Title>
      <InputWrapper focus={focus}>
        <Icon name="search" style={{ marginRight: '10px' }} />
        <SearchInput
          value={searchText}
          onChange={({ target: { value } }) => setSearchText(value)}
          placeholder={currNamePlaceholder}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
        />
      </InputWrapper>

      {currencyCurr.daily.length > 0 && (
        <>
          <TitleWrapper>
            <Desc>
              <FormattedMessage id="table.abotherCurr.daily" />
            </Desc>
            <Update>
              <Icon name="watch" style={{ margin: '0 5px 1px 0' }} />
              <FormattedMessage id="updated" />
              {` `}
              {data.daily[0]?.date &&
                data.daily[0]?.time &&
                dayjs(
                  `${data.daily[0].date} ${data.daily[0].time}`,
                  'DD.MM.YYYY HH:mm'
                ).format('DD.MM.YYYY, HH:mm')}
            </Update>
          </TitleWrapper>
          <Table
            titles={titles}
            rows={currencyCurr.daily}
            activeCurrency={currency}
            getHref={getHref}
            type="nbu"
            anotherCurrTable
          />
          <TableFooter>
            <Source>
              <FormattedMessage id="barGraph.source" />
            </Source>
          </TableFooter>
        </>
      )}

      {currencyCurr.monthly.length > 0 && (
        <>
          <TitleWrapper>
            <Desc>
              <FormattedMessage id="table.abotherCurr.monthly" />
            </Desc>
            <Update>
              <Icon name="watch" style={{ margin: '0 5px 1px 0' }} />
              <FormattedMessage id="updated" />
              {` `}
              {data.monthly[0]?.date &&
                data.monthly[0]?.time &&
                dayjs(
                  `${data.monthly[0].date} ${data.monthly[0].time}`,
                  'DD.MM.YYYY HH:mm'
                ).format('DD.MM.YYYY, HH:mm')}
            </Update>
          </TitleWrapper>

          <Table
            titles={titles}
            rows={currencyCurr.monthly}
            activeCurrency={currency}
            getHref={getHref}
            type="nbu"
            anotherCurrTable
          />

          <TableFooter>
            <Source>
              <FormattedMessage id="barGraph.source" />
            </Source>
          </TableFooter>
        </>
      )}

      {currencyCurr?.monthly.length <= 0 && currencyCurr?.daily.length <= 0 ? (
        <FormattedMessage id="noData.noFound" />
      ) : null}
    </Wrapper>
  );
};

export default AnotherCurrTable;
